// src/components/vehicles/VehicleDetail.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api, formatDate, formatCurrency, daysLeftText, getDeadlineColor, getDeadlineLabel } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";

  const [vehicle, setVehicle] = useState(null);
  const [services, setServices] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [repairCategories, setRepairCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costSummary, setCostSummary] = useState(null);

  const [serviceDialogVisible, setServiceDialogVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    serviceTypeId: null, performedDate: null, validUntil: null,
    warningDaysBefore: 14, cost: null, notes: "",
  });

  const [repairDialogVisible, setRepairDialogVisible] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [repairForm, setRepairForm] = useState({
    repairCategoryId: null, repairDate: null, cost: 0, description: "",
  });

  // Renewal
  const [renewDialogVisible, setRenewDialogVisible] = useState(false);
  const [renewingService, setRenewingService] = useState(null);
  const [renewForm, setRenewForm] = useState({ performedDate: null, validUntil: null, warningDaysBefore: 30, cost: null, notes: "" });
  const [renewLoading, setRenewLoading] = useState(false);

  // History
  const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
  const [historyTypeName, setHistoryTypeName] = useState("");
  const [historyRecords, setHistoryRecords] = useState([]);

  useEffect(() => { loadAll(); }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [vehicleRes, servicesRes, repairsRes, stRes, rcRes, costRes] = await Promise.all([
        api.get(`/vehicles/${id}`), api.get(`/vehicles/${id}/services`),
        api.get(`/vehicles/${id}/repairs`), api.get("/service-types"), api.get("/repair-categories"),
        api.get(`/vehicles/${id}/cost-summary`),
      ]);
      setVehicle(vehicleRes.data); setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setRepairs(Array.isArray(repairsRes.data) ? repairsRes.data : []); setServiceTypes(Array.isArray(stRes.data) ? stRes.data : []); setRepairCategories(Array.isArray(rcRes.data) ? rcRes.data : []);
      setCostSummary(costRes.data);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: "Nie udało się załadować danych" });
    }
    setLoading(false);
  };

  const getServiceOverview = () => {
    const overview = {};
    services.forEach((s) => {
      const key = s.service_type_id;
      if (!overview[key] || new Date(s.performed_date) > new Date(overview[key].performed_date)) overview[key] = s;
    });
    return Object.values(overview).map((s) => {
      const daysLeft = s.valid_until ? Math.ceil((new Date(s.valid_until) - new Date()) / (1000 * 60 * 60 * 24)) : null;
      const status = daysLeft === null ? "ok" : daysLeft <= 0 ? "overdue" : daysLeft <= s.warning_days_before ? "warning" : "ok";
      return { ...s, daysLeft, deadlineStatus: status };
    });
  };

  // ---- Service CRUD ----
  const openNewService = () => {
    setEditingService(null);
    setServiceForm({ serviceTypeId: null, performedDate: null, validUntil: null, warningDaysBefore: 14, cost: null, notes: "" });
    setServiceDialogVisible(true);
  };

  const openEditService = (s) => {
    setEditingService(s);
    setServiceForm({
      serviceTypeId: s.service_type_id, performedDate: new Date(s.performed_date),
      validUntil: s.valid_until ? new Date(s.valid_until) : null,
      warningDaysBefore: s.warning_days_before, cost: s.cost, notes: s.notes || "",
    });
    setServiceDialogVisible(true);
  };

  const saveService = async () => {
    if (!serviceForm.serviceTypeId || !serviceForm.performedDate) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Typ i data wykonania są wymagane" }); return;
    }
    try {
      if (editingService) await api.put(`/services/${editingService.id}`, serviceForm);
      else await api.post(`/vehicles/${id}/services`, serviceForm);
      setServiceDialogVisible(false);
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Serwis zapisany" });
      loadAll();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        toast.current.show({ severity: "warn", summary: "Uwaga", detail: err.response.data?.message || "Ten typ serwisu jest odnawialny i już istnieje — użyj przedłużenia." });
      } else {
        toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
      }
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm("Usunąć ten wpis serwisu?")) return;
    try {
      await api.delete(`/services/${serviceId}`);
      toast.current.show({ severity: "success", summary: "Usunięto" });
      loadAll();
    } catch (err) { console.error(err); }
  };

  // ---- Renewable helpers ----
  const getAvailableServiceTypesForAdd = () => {
    // For new service: hide renewable types that already have a record on this vehicle
    const existingRenewableTypeIds = new Set();
    services.forEach(s => {
      const st = serviceTypes.find(t => t.id === s.service_type_id);
      if (st?.is_renewable) existingRenewableTypeIds.add(st.id);
    });
    return serviceTypes.filter(t => !t.is_renewable || !existingRenewableTypeIds.has(t.id));
  };

  // ---- Renew ----
  const openRenew = async (service) => {
    setRenewingService(service);
    setRenewLoading(true);
    setRenewDialogVisible(true);
    try {
      const res = await api.get(`/services/${service.id}/renew-defaults`);
      const d = res.data;
      setRenewForm({
        performedDate: d.performedDate ? new Date(d.performedDate) : null,
        validUntil: d.validUntil ? new Date(d.validUntil) : null,
        warningDaysBefore: d.warningDaysBefore || 30,
        cost: null,
        notes: "",
      });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: "Nie udało się pobrać domyślnych dat" });
    }
    setRenewLoading(false);
  };

  const saveRenew = async () => {
    if (!renewingService) return;
    try {
      await api.post(`/services/${renewingService.id}/renew`, {
        performedDate: renewForm.performedDate,
        validUntil: renewForm.validUntil,
        warningDaysBefore: renewForm.warningDaysBefore,
        cost: renewForm.cost,
        notes: renewForm.notes,
      });
      setRenewDialogVisible(false);
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Serwis przedłużony" });
      loadAll();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się przedłużyć" });
    }
  };

  // ---- Service history per type ----
  const openHistory = (serviceTypeId, serviceTypeName) => {
    const records = services.filter(s => s.service_type_id === serviceTypeId)
      .sort((a, b) => new Date(b.performed_date) - new Date(a.performed_date));
    setHistoryRecords(records);
    setHistoryTypeName(serviceTypeName);
    setHistoryDialogVisible(true);
  };

  // ---- Repair CRUD ----
  const openNewRepair = () => {
    setEditingRepair(null);
    setRepairForm({ repairCategoryId: null, repairDate: null, cost: 0, description: "" });
    setRepairDialogVisible(true);
  };

  const openEditRepair = (r) => {
    setEditingRepair(r);
    setRepairForm({
      repairCategoryId: r.repair_category_id, repairDate: new Date(r.repair_date),
      cost: parseFloat(r.cost), description: r.description,
    });
    setRepairDialogVisible(true);
  };

  const saveRepair = async () => {
    if (!repairForm.repairDate || !repairForm.description) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Data i opis są wymagane" }); return;
    }
    try {
      if (editingRepair) await api.put(`/repairs/${editingRepair.id}`, repairForm);
      else await api.post(`/vehicles/${id}/repairs`, repairForm);
      setRepairDialogVisible(false);
      toast.current.show({ severity: "success", summary: "Sukces", detail: "Naprawa zapisana" });
      loadAll();
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
    }
  };

  const deleteRepair = async (repairId) => {
    if (!window.confirm("Usunąć tę naprawę?")) return;
    try {
      await api.delete(`/repairs/${repairId}`);
      toast.current.show({ severity: "success", summary: "Usunięto" });
      loadAll();
    } catch (err) { console.error(err); }
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="fleet-tooltip">
          <p style={{ margin: "0 0 0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ margin: 0, color: p.color, fontSize: "0.8rem" }}>
              {p.name}: {p.name === "Koszt" ? formatCurrency(p.value) : p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="fleet-loading"><ProgressSpinner /></div>;
  if (!vehicle) return <div className="fleet-page"><div className="fleet-empty-state"><i className="pi pi-car" /><p>Pojazd nie znaleziony</p></div></div>;

  const serviceOverview = getServiceOverview();
  const overdueCount = serviceOverview.filter(s => s.deadlineStatus === "overdue").length;
  const warningCount = serviceOverview.filter(s => s.deadlineStatus === "warning").length;

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Back + Header */}
      <button className="fleet-back-btn" onClick={() => navigate("/pojazdy/lista")}>
        <i className="pi pi-arrow-left" /> Powrót do listy
      </button>

      <div className="fleet-page-header" style={{ marginTop: "0.75rem" }}>
        <div>
          <h1>
            {vehicle.brand} {vehicle.model}
            <span className={`fleet-tag ${vehicle.status === "ACTIVE" ? "fleet-tag--success" : "fleet-tag--danger"}`} style={{ marginLeft: "0.75rem", verticalAlign: "middle", fontSize: "0.7rem" }}>
              <span className={`fleet-status-dot ${vehicle.status === "ACTIVE" ? "fleet-status-dot--active" : "fleet-status-dot--inactive"}`} />
              {vehicle.status === "ACTIVE" ? "Aktywny" : "Nieaktywny"}
            </span>
          </h1>
          <p className="fleet-page-subtitle">{vehicle.registration_number} · {vehicle.vehicleTypeName} {vehicle.companyName ? `· ${vehicle.companyName}` : ""}</p>
        </div>
      </div>

      {/* Cost Summary Stat Cards */}
      {costSummary && (
        <div className="fleet-stats-grid fleet-stats-grid--4">
          {isAdmin && <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-wallet" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary" style={{ fontSize: "1.25rem" }}>{formatCurrency(costSummary.totalCost)}</div>
            <div className="fleet-stat-label">Łączny koszt</div>
            <div className="fleet-stat-sub">serwisy + naprawy</div>
          </div>}

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--success">
            <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-wrench" /></div>
            <div className="fleet-stat-value fleet-stat-value--success" style={{ fontSize: "1.25rem" }}>{formatCurrency(costSummary.totalServiceCost)}</div>
            <div className="fleet-stat-label">Koszt serwisów</div>
            <div className="fleet-stat-sub">{costSummary.serviceCount} wpisów</div>
          </div>}

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--danger">
            <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-cog" /></div>
            <div className="fleet-stat-value fleet-stat-value--danger" style={{ fontSize: "1.25rem" }}>{formatCurrency(costSummary.totalRepairCost)}</div>
            <div className="fleet-stat-label">Koszt napraw</div>
            <div className="fleet-stat-sub">{costSummary.repairCount} napraw</div>
          </div>}

          <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-shield" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning">{serviceOverview.length}</div>
            <div className="fleet-stat-label">Typy serwisów</div>
            <div className="fleet-stat-sub">
              {overdueCount > 0 && <span style={{ color: "var(--fleet-danger)" }}>{overdueCount} przetermin.</span>}
              {overdueCount > 0 && warningCount > 0 && " · "}
              {warningCount > 0 && <span style={{ color: "var(--fleet-warning)" }}>{warningCount} ostrzeż.</span>}
              {overdueCount === 0 && warningCount === 0 && "wszystko OK"}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Info Card */}
      <div className="fleet-card" style={{ marginBottom: "1.25rem" }}>
        <div className="fleet-card-header">
          <h3 className="fleet-card-title"><i className="pi pi-id-card" /> Informacje o pojeździe</h3>
        </div>
        <div className="fleet-card-body">
          <div className="fleet-info-grid">
            <div className="fleet-info-item">
              <span className="fleet-info-label">Nr rejestracyjny</span>
              <span className="fleet-info-value">{vehicle.registration_number}</span>
            </div>
            <div className="fleet-info-item">
              <span className="fleet-info-label">Typ</span>
              <span className="fleet-info-value">{vehicle.vehicleTypeName}</span>
            </div>
            <div className="fleet-info-item">
              <span className="fleet-info-label">VIN</span>
              <span className="fleet-info-value">{vehicle.vin || "—"}</span>
            </div>
            <div className="fleet-info-item">
              <span className="fleet-info-label">Data rejestracji</span>
              <span className="fleet-info-value">{formatDate(vehicle.registration_date)}</span>
            </div>
            <div className="fleet-info-item">
              <span className="fleet-info-label">Firma</span>
              <span className="fleet-info-value">{vehicle.companyName || "—"}</span>
            </div>
            <div className="fleet-info-item">
              <span className="fleet-info-label">Kierowca / Operator</span>
              <span className="fleet-info-value">{vehicle.driver_operator || "—"}</span>
            </div>
            {vehicle.notes && (
              <div className="fleet-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="fleet-info-label">Notatki</span>
                <span className="fleet-info-value">{vehicle.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Status Cards */}
      {serviceOverview.length > 0 && (
        <div className="fleet-service-status-grid">
          {serviceOverview.map((s) => {
            const cls = s.deadlineStatus === "overdue" ? "fleet-service-status-card--overdue" : s.deadlineStatus === "warning" ? "fleet-service-status-card--warning" : "fleet-service-status-card--ok";
            const tagCls = s.deadlineStatus === "overdue" ? "fleet-tag--danger" : s.deadlineStatus === "warning" ? "fleet-tag--warning" : "fleet-tag--success";
            const sType = serviceTypes.find(t => t.id === s.service_type_id);
            const isRenewable = sType?.is_renewable;
            const historyCount = services.filter(r => r.service_type_id === s.service_type_id).length;
            return (
              <div key={s.id} className={`fleet-service-status-card ${cls}`}>
                <div style={{ flex: 1 }}>
                  <div className="fleet-service-name">
                    {s.serviceTypeName}
                    {isRenewable && <i className="pi pi-sync" style={{ marginLeft: "0.4rem", fontSize: "0.75rem", opacity: 0.6 }} title="Odnawialny" />}
                  </div>
                  <div className="fleet-service-date">Ważne do: {formatDate(s.valid_until)}</div>
                  {historyCount > 1 && (
                    <button
                      onClick={() => openHistory(s.service_type_id, s.serviceTypeName)}
                      style={{ background: "none", border: "none", padding: 0, color: "var(--fleet-primary)", fontSize: "0.75rem", cursor: "pointer", marginTop: "0.2rem", textDecoration: "underline" }}
                    >
                      <i className="pi pi-history" style={{ fontSize: "0.7rem" }} /> Historia ({historyCount})
                    </button>
                  )}
                </div>
                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.35rem" }}>
                  <span className={`fleet-tag ${tagCls}`}>{getDeadlineLabel(s.deadlineStatus)}</span>
                  {s.daysLeft !== null && (
                    <div className="fleet-service-days" style={{ color: getDeadlineColor(s.deadlineStatus) }}>
                      {daysLeftText(s.daysLeft)}
                    </div>
                  )}
                  {isRenewable && (
                    <Button label="Przedłuż" icon="pi pi-refresh" size="small" className="fleet-btn-primary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }} onClick={() => openRenew(s)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cost Charts */}
      {costSummary && (costSummary.monthlyRepairs?.length > 0 || costSummary.repairsByCategory?.length > 0) && (
        <div className="fleet-dashboard-grid" style={{ marginBottom: "1.25rem" }}>
          {costSummary.monthlyRepairs?.length > 0 && (
            <div className="fleet-chart-card">
              <div className="fleet-chart-title"><i className="pi pi-chart-bar" /> Koszty napraw w czasie</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={costSummary.monthlyRepairs}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--fleet-text-secondary)" }} />
                  <YAxis tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} tick={{ fontSize: 11, fill: "var(--fleet-text-muted)" }} />
                  <Tooltip content={customTooltip} />
                  <Bar dataKey="totalCost" name="Koszt" fill="var(--fleet-primary)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {costSummary.repairsByCategory?.length > 0 && (
            <div className="fleet-chart-card">
              <div className="fleet-chart-title"><i className="pi pi-chart-pie" /> Naprawy wg kategorii</div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={costSummary.repairsByCategory} dataKey="totalCost" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={40}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: "var(--fleet-text-muted)" }}>
                    {costSummary.repairsByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => formatCurrency(val)} />
                  <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Tabs — Services & Repairs */}
      <div className="fleet-tabs">
        <TabView>
          <TabPanel header={`Serwisy (${services.length})`}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
              <Button label="Dodaj serwis" icon="pi pi-plus" className="fleet-btn-primary" onClick={openNewService} />
            </div>
            <div className="fleet-card">
              <div className="fleet-card-body--flush fleet-table">
                <DataTable value={services} emptyMessage="Brak wpisów serwisowych" responsiveLayout="stack" breakpoint="768px">
                  <Column field="serviceTypeName" header="Typ serwisu" />
                  <Column header="Data wykonania" body={(row) => formatDate(row.performed_date)} />
                  <Column header="Ważne do" body={(row) => formatDate(row.valid_until)} />
                  <Column header="Ostrzeżenie" body={(row) => `${row.warning_days_before} dni`} />
                  <Column header="Koszt" body={(row) => formatCurrency(row.cost)} />
                  <Column field="notes" header="Notatki" />
                  <Column header="Akcje" body={(row) => (
                    <div className="fleet-actions">
                      <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => openEditService(row)} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
                      <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => deleteService(row.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
                    </div>
                  )} style={{ width: "100px" }} />
                </DataTable>
              </div>
            </div>
          </TabPanel>

          <TabPanel header={`Naprawy (${repairs.length})`}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
              <Button label="Dodaj naprawę" icon="pi pi-plus" className="fleet-btn-primary" onClick={openNewRepair} />
            </div>
            <div className="fleet-card">
              <div className="fleet-card-body--flush fleet-table">
                <DataTable value={repairs} emptyMessage="Brak napraw" responsiveLayout="stack" breakpoint="768px">
                  <Column header="Data" body={(row) => formatDate(row.repair_date)} sortable sortField="repair_date" />
                  <Column field="categoryName" header="Kategoria" />
                  <Column header="Koszt" body={(row) => formatCurrency(row.cost)} />
                  <Column field="description" header="Opis" style={{ maxWidth: "300px" }} />
                  <Column field="createdByName" header="Dodał" />
                  <Column header="Akcje" body={(row) => (
                    <div className="fleet-actions">
                      <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => openEditRepair(row)} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
                      <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => deleteRepair(row.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
                    </div>
                  )} style={{ width: "100px" }} />
                </DataTable>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>

      {/* Service Dialog */}
      <Dialog
        header={editingService ? "Edytuj serwis" : "Nowy serwis"}
        visible={serviceDialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setServiceDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setServiceDialogVisible(false)} />
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" onClick={saveService} />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Typ serwisu *</label>
            <Dropdown value={serviceForm.serviceTypeId} options={editingService ? serviceTypes : getAvailableServiceTypesForAdd()} onChange={(e) => setServiceForm({ ...serviceForm, serviceTypeId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz" disabled={!!editingService} />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Data wykonania *</label>
              <Calendar value={serviceForm.performedDate} onChange={(e) => setServiceForm({ ...serviceForm, performedDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>Ważne do</label>
              <Calendar value={serviceForm.validUntil} onChange={(e) => setServiceForm({ ...serviceForm, validUntil: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Ostrzeżenie (dni przed)</label>
              <InputNumber value={serviceForm.warningDaysBefore} onValueChange={(e) => setServiceForm({ ...serviceForm, warningDaysBefore: e.value })} min={1} max={365} />
            </div>
            <div className="fleet-form-field">
              <label>Koszt (PLN)</label>
              <InputNumber value={serviceForm.cost} onValueChange={(e) => setServiceForm({ ...serviceForm, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Notatki</label>
            <InputTextarea value={serviceForm.notes} onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })} rows={3} />
          </div>
        </div>
      </Dialog>

      {/* Repair Dialog */}
      <Dialog
        header={editingRepair ? "Edytuj naprawę" : "Nowa naprawa"}
        visible={repairDialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setRepairDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setRepairDialogVisible(false)} />
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" onClick={saveRepair} />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Kategoria</label>
            <Dropdown value={repairForm.repairCategoryId} options={repairCategories} onChange={(e) => setRepairForm({ ...repairForm, repairCategoryId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz" showClear />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Data naprawy *</label>
              <Calendar value={repairForm.repairDate} onChange={(e) => setRepairForm({ ...repairForm, repairDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>Koszt (PLN) *</label>
              <InputNumber value={repairForm.cost} onValueChange={(e) => setRepairForm({ ...repairForm, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Opis *</label>
            <InputTextarea value={repairForm.description} onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })} rows={4} />
          </div>
        </div>
      </Dialog>

      {/* Renew Service Dialog */}
      <Dialog
        header={renewingService ? `Przedłuż: ${renewingService.serviceTypeName}` : "Przedłuż serwis"}
        visible={renewDialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setRenewDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setRenewDialogVisible(false)} />
            <Button label="Przedłuż" icon="pi pi-refresh" className="fleet-btn-primary" onClick={saveRenew} loading={renewLoading} />
          </div>
        }
      >
        {renewLoading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}><ProgressSpinner style={{ width: "40px", height: "40px" }} /></div>
        ) : (
          <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            {renewingService && (
              <div style={{ background: "var(--fleet-info-bg, #eff6ff)", padding: "0.75rem 1rem", borderRadius: "var(--fleet-radius-sm)", fontSize: "0.85rem", color: "var(--fleet-text-secondary)" }}>
                <i className="pi pi-info-circle" style={{ marginRight: "0.5rem" }} />
                Poprzedni serwis ważny do: <strong>{formatDate(renewingService.valid_until)}</strong>.
                Nowy okres rozpocznie się dzień po zakończeniu poprzedniego.
              </div>
            )}
            <div className="fleet-form-grid">
              <div className="fleet-form-field">
                <label>Data rozpoczęcia *</label>
                <Calendar value={renewForm.performedDate} onChange={(e) => setRenewForm({ ...renewForm, performedDate: e.value })} dateFormat="dd.mm.yy" showIcon />
              </div>
              <div className="fleet-form-field">
                <label>Ważne do *</label>
                <Calendar value={renewForm.validUntil} onChange={(e) => setRenewForm({ ...renewForm, validUntil: e.value })} dateFormat="dd.mm.yy" showIcon />
              </div>
            </div>
            <div className="fleet-form-grid">
              <div className="fleet-form-field">
                <label>Ostrzeżenie (dni przed)</label>
                <InputNumber value={renewForm.warningDaysBefore} onValueChange={(e) => setRenewForm({ ...renewForm, warningDaysBefore: e.value })} min={1} max={365} />
              </div>
              <div className="fleet-form-field">
                <label>Koszt (PLN)</label>
                <InputNumber value={renewForm.cost} onValueChange={(e) => setRenewForm({ ...renewForm, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
              </div>
            </div>
            <div className="fleet-form-field">
              <label>Notatki</label>
              <InputTextarea value={renewForm.notes} onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
        )}
      </Dialog>

      {/* Service History Dialog */}
      <Dialog
        header={`Historia: ${historyTypeName}`}
        visible={historyDialogVisible}
        style={{ width: "700px", maxWidth: "95vw" }}
        onHide={() => setHistoryDialogVisible(false)}
        className="fleet-dialog"
      >
        <div className="fleet-table">
          <DataTable value={historyRecords} emptyMessage="Brak historii" size="small" sortField="performed_date" sortOrder={-1}>
            <Column header="Data wykonania" body={(row) => formatDate(row.performed_date)} sortable sortField="performed_date" />
            <Column header="Ważne do" body={(row) => formatDate(row.valid_until)} />
            <Column header="Koszt" body={(row) => formatCurrency(row.cost)} />
            <Column field="notes" header="Notatki" />
            <Column header="Status" body={(row) => {
              const daysLeft = row.valid_until ? Math.ceil((new Date(row.valid_until) - new Date()) / (1000*60*60*24)) : null;
              if (daysLeft === null) return "—";
              if (daysLeft <= 0) return <span className="fleet-tag fleet-tag--danger">Wygasły</span>;
              return <span className="fleet-tag fleet-tag--success">Aktywny</span>;
            }} />
          </DataTable>
        </div>
      </Dialog>
    </div>
  );
};

export default VehicleDetail;
